import React, { useEffect } from 'react';
import './App.css';
import '@rainbow-me/rainbowkit/styles.css';
import { ThemeProvider } from './context/ThemeContext';
import { WagmiProvider } from 'wagmi';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { RainbowKitProvider, darkTheme, lightTheme } from '@rainbow-me/rainbowkit';
import { useAccount } from 'wagmi';
import { config } from './wagmi';
import { useTheme } from './context/ThemeContext';
import { useAutoChainSwitch } from './hooks/useAutoChainSwitch';
import Header from './components/Header';
import ChainNotificationBanner from './components/ChainNotificationBanner';
import ConnectWalletBlock from './components/ConnectWalletBlock';
import ConnectedWalletContent from './components/ConnectedWalletContent';
import ConnectedWalletContentNoBalance from './components/ConnectedWalletContentNoBalance';
import CampaignRow from './components/CampaignRow';
import SablierCampaignRow from './components/SablierCampaignRow';
import StakeBanner from './components/StakeBanner';
import NewsletterBlock from './components/NewsletterBlock';
import HelpCenterBlock from './components/HelpCenterBlock';
import Footer from './components/Footer';
import { isAddressEligible } from './utils/rewardsCalculator';
import {
  getOngoingCampaigns,
  getPreviousCampaigns,
  getSablierCampaigns,
} from './utils/campaignData';
import blueGradientDark from './assets/png/blue_gradient-dark.png';
import blueGradientLight from './assets/png/blue_gradient-light.png';
import blueGradientMobileDark from './assets/png/blue_gradient-mobile_dark.png';

// Campaign types
export interface Campaign {
  link: string;
  code: string;
  title: string;
  status: string;
  statusColor: string;
  rewards?: string;
  buttonText: string;
  rewardsText?: string;
  tooltip?: string;
  dateEnd?: string;
}

const queryClient = new QueryClient();

function AppContent(): React.JSX.Element {
  const { theme } = useTheme();
  const { isConnected, address } = useAccount();
  const { isSwitching, showBanner } = useAutoChainSwitch();
  const [isBannerClosed, setIsBannerClosed] = React.useState(false);

  // Check if the connected address is eligible based on eligible.json
  const hasBalance = isAddressEligible(address);
  const ongoingCampaigns = getOngoingCampaigns();
  const previousCampaigns = getPreviousCampaigns();
  const sablierCampaigns = getSablierCampaigns();

  return (
    <RainbowKitProvider theme={theme === 'dark' ? darkTheme() : lightTheme()}>
      <div className="relative min-h-screen overflow-x-hidden transition-colors">
        {/* Theme-based radial gradient background effect */}
        {theme === 'dark' ? (
          <div className="absolute top-0 z-[-2] h-screen w-screen bg-neutral-950 bg-[radial-gradient(ellipse_80%_80%_at_50%_-20%,rgba(120,119,198,0.3),rgba(255,255,255,0))]"></div>
        ) : (
          <div className="absolute top-0 z-[-2] h-screen w-screen bg-white bg-[radial-gradient(100%_50%_at_50%_0%,rgba(0,163,255,0.13)_0,rgba(0,163,255,0)_50%,rgba(0,163,255,0)_100%)]"></div>
        )}

        {/* Background gradient effect */}
        <div className="absolute left-0 right-0 top-0 h-[300px] bg-gradient-to-b from-primary-azul/10 via-transparent to-transparent" />

        {/* Theme-based blue gradient background - Desktop */}
        <div
          className="hidden md:block absolute left-1/2 transform -translate-x-1/2 w-full max-w-[1200px] h-[400px] bg-no-repeat bg-center opacity-80"
          style={{
            backgroundImage: `url(${theme === 'dark' ? blueGradientDark : blueGradientLight})`,
            backgroundSize: 'contain',
            top: '-135px',
          }}
        />

        {/* Mobile blue gradient background */}
        <div
          className="block md:hidden absolute left-1/2 transform -translate-x-1/2 w-full h-[400px] bg-no-repeat bg-center opacity-80"
          style={{
            backgroundImage: `url(${theme === 'dark' ? blueGradientMobileDark : blueGradientLight})`,
            backgroundSize: 'contain',
            top: '-135px',
          }}
        />

        {/* Chain Notification Banner - positioned at very top */}
        {showBanner && !isBannerClosed && (
          <ChainNotificationBanner
            isSwitching={isSwitching}
            onClose={() => setIsBannerClosed(true)}
          />
        )}

        {/* Header container - positioned after banner */}
        <div className={`relative ${showBanner && !isBannerClosed ? 'top-9 md:top-10' : 'top-0'}`}>
          <Header />
        </div>

        {/* Main Content - Adjust padding based on banner presence */}
        <div
          className={`relative pb-[60px] md:pb-[80px] min-h-screen ${
            showBanner && !isBannerClosed ? 'pt-[113px] md:pt-[128px]' : 'pt-[68px] md:pt-[88px]'
          }`}
        >
          {/* Title */}
          <div className="text-center mt-10 md:mt-20 mb-12 px-4 md:px-0">
            <h1 className="text-contrast-3 dark:text-white text-[26px] md:text-[32px] font-medium font-geist">
              Velora Token Distribution
            </h1>
          </div>

          {/* Content Container - Conditional rendering based on wallet connection */}
          {!isConnected ? (
            <div className="max-w-[1264px] mx-auto px-4 md:px-8">
              {/* Connect Wallet Section */}
              <ConnectWalletBlock />

              {/* Campaigns and Stake Section */}
              <div className="flex flex-col md:flex-row gap-8 mt-8 md:mt-[60px]">
                {/* Campaigns Column */}
                <div className="flex-1">
                  {/* Ongoing Campaigns */}
                  <div className="mb-12">
                    <h2 className="text-contrast-3 dark:text-contrast-8 text-[20px] md:text-[24px] font-medium font-geist mb-6">
                      Ongoing Campaigns
                    </h2>
                    <div className="space-y-4">
                      {/* Sablier campaigns */}
                      {sablierCampaigns.map((campaign, index) => (
                        <SablierCampaignRow key={`sablier-${index}`} campaign={campaign} />
                      ))}
                      {/* Regular ongoing campaigns */}
                      {ongoingCampaigns.map((campaign, index) => (
                        <CampaignRow key={`regular-${index}`} campaign={campaign} type="ongoing" />
                      ))}
                    </div>
                  </div>

                  {/* Previous Campaigns */}
                  <div>
                    <h2 className="text-contrast-3 dark:text-contrast-8 text-[20px] md:text-[24px] font-medium font-geist mb-6">
                      Previous Campaigns
                    </h2>
                    <div className="space-y-4">
                      {previousCampaigns.map((campaign, index) => (
                        <CampaignRow key={index} campaign={campaign} type="previous" />
                      ))}
                    </div>
                  </div>
                </div>

                {/* Stake Banner */}
                <StakeBanner />
              </div>

              {/* Newsletter and Help Center Section */}
              <div className="flex flex-col md:flex-row gap-6 md:gap-[30px] mt-8 md:mt-[60px]">
                <NewsletterBlock />
                <HelpCenterBlock />
              </div>
            </div>
          ) : /* Connected Wallet Content - show different component based on eligibility */
          hasBalance ? (
            <ConnectedWalletContent />
          ) : (
            <ConnectedWalletContentNoBalance />
          )}

          <div className="mt-16 md:mt-[120px]">
            <Footer />
          </div>
        </div>
      </div>
    </RainbowKitProvider>
  );
}

function App(): React.JSX.Element {
  useEffect(() => {
    const styles = document.createElement('style');
    styles.innerHTML = `
    @keyframes slowRotate {
  from {
    transform: rotate(0deg) translate(-50%);
  }
  to {
    transform: rotate(360deg) translate(-50%);
  }
}
       #veloraBckImg {
        display: block;
        position: absolute;
        top: 0;
        left: 50%;
        z-index: -1;
        animation: slowRotate 4.8s linear infinite;
        transform-origin: 0 40%;
        filter: blur(130px)
      }
      /* // prevent img from creating overflow scroll on body when rotating */
      #veloraBckImgContainer {
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        overflow: hidden;
      }

      #root {
        background-image: linear-gradient(0deg, rgba(234, 233, 237, 0.8) 0%, rgba(255, 255, 255, 0.4) 100%);
      }
      .dark #root {
        background-image: linear-gradient(0deg, rgb(15, 17, 26) 0%, rgba(21, 23, 36, 0.7) 100%);
}
  
    `;
    document.head.appendChild(styles);

    // Cleanup: remove the style tag on unmount
    return () => {
      document.head.removeChild(styles);
    };
  }, []);
  return (
    <ThemeProvider>
      <WagmiProvider config={config}>
        <QueryClientProvider client={queryClient}>
          <AppContent />
        </QueryClientProvider>
      </WagmiProvider>
    </ThemeProvider>
  );
}

export default App;
